import React, { memo, useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import invert from 'lodash/invert';
import isNil from 'lodash/isNil';
import { token } from '@atlaskit/tokens';
import {
	type StatusCategoryKeys,
	StatusCategoryTypes,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import type { StatusValue } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { IssueStatusFieldBoundary } from '@atlassian/jira-issue-field-status/src/ui/async.tsx';
import type IssueStatusFieldType from '@atlassian/jira-issue-field-status/src/ui/main.tsx';
import { StatusKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import {
	cancelInlineEditStatusInteractionMark,
	setInlineEditStatusInteractionStartMark,
	setInlineEditStatusInteractionStopMark,
} from '../../../../../services/performance/interaction-metric-inline-edit.tsx';
import { cardStatusSet } from '../../../../../state/actions/status/index.tsx';
import { refetchVersions } from '../../../../../state/actions/version/index.tsx';
import { useBacklogDispatch } from '../../../../../state/index.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import SLAExperienceTracker from '../../../../sla-experience-tracker/index.tsx';
import StatusFieldStatic from './status-field-static/index.tsx';

// Exporting to mock in test file
export const IssueStatusField = lazyForPaint<typeof IssueStatusFieldType>(
	() =>
		import(/* webpackChunkName: "async-issue-field-status" */ '@atlassian/jira-issue-field-status'),
);

export type Props = {
	isUserAtLeastMember: boolean;
	issueKey: string;
	issueId?: string;
	shouldRenderRichCard: boolean;
	widthMultiplier: number;
};

const StatusField = ({
	isUserAtLeastMember,
	shouldRenderRichCard,
	issueKey,
	widthMultiplier,
	issueId,
}: Props) => {
	const dispatch = useBacklogDispatch();
	const [statusFieldValue] = useFieldValue({
		issueKey,
		fieldKey: StatusKey,
	});

	const isCompanyManaged = useIsCMPBacklog();

	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();

	const handleSubmit = useCallback(() => {
		setInlineEditStatusInteractionStartMark();
	}, []);

	const handleSuccess = useCallback(
		(value: StatusValue) => {
			setInlineEditStatusInteractionStopMark(isCompanyManaged);
			dispatch(refetchVersions());

			if (issueId !== undefined) {
				const cardId = Number.parseInt(issueId, 10);
				dispatch(
					cardStatusSet(
						cardId,
						statusFieldValue.id,
						value.id,
						statusFieldValue.id,
						isCompanyManaged,
					),
				);
			}
			if (isCompanyManaged) {
				triggerEpicPanelRefetch();
			}
		},
		[dispatch, isCompanyManaged, issueId, statusFieldValue, triggerEpicPanelRefetch],
	);

	const handleFailure = useCallback(() => {
		cancelInlineEditStatusInteractionMark();
	}, []);

	if (isNil(statusFieldValue) || !statusFieldValue.id || !statusFieldValue.name) {
		return null;
	}

	const value = {
		id: statusFieldValue.id,
		name: statusFieldValue.name,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		category: (invert(StatusCategoryIds)[statusFieldValue.statusCategory.id] ||
			StatusCategoryTypes.UNDEFINED) as StatusCategoryKeys,
	};
	const issueStatusFieldStatic = (
		<StatusFieldStatic isUserAtLeastMember={isUserAtLeastMember} value={value} />
	);

	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<IssueStatusFieldBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={issueStatusFieldStatic}
			>
				<SLAExperienceTracker
					experienceTarget="inline status"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<StatusLozengeWrapper widthMultiplier={widthMultiplier}>
						<IssueStatusField
							appearance="lozenge"
							issueKey={issueKey}
							issueId={issueId}
							initialValue={statusFieldValue}
							onSubmit={handleSubmit}
							onSuccess={handleSuccess}
							onFailure={handleFailure}
						/>
					</StatusLozengeWrapper>
				</SLAExperienceTracker>
			</IssueStatusFieldBoundary>
		);
	}

	return issueStatusFieldStatic;
};

export default memo<Props>(StatusField);

// NOTE: The div is needed here because StatusLozenge is an async component and
// we use a universal selector hack in the styling.
//
// There's a chance that the margin won't apply to the component properly
// without an anchor container here because the async component is quickly
// switching on mouse hover
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const StatusLozengeWrapper = styled2.div<{ widthMultiplier: number }>({
	/* NOTE: This is needed to ensure the dropdown menu is right aligned */
	position: 'relative',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-of-type': {
			display: 'grid',
			alignItems: 'center',
			justifyItems: 'start',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		'& > div': ({ widthMultiplier }) => ({
			pointerEvents: 'all',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
			maxWidth: () =>
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				isVisualRefreshEnabled() ? `${widthMultiplier * 8 - 4}px` : `${widthMultiplier * 8}px`,
		}),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > div > button': {
			/* This button element is styled as an inline-flex in atlaskit. So, it's not perfectly vertically centerable without this.
			Update: with visual refresh change to pressable, this is no longer an issue
			*/
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			verticalAlign: 'top !important', // not perfectly vertically centered without this
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values
			marginTop: () => (isVisualRefreshEnabled() ? '2px' : 0),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values
			marginLeft: () => (isVisualRefreshEnabled() ? '2px' : 0),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:nth-of-type(2)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			position: 'absolute !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			top: `${token('space.300', '24px')} !important`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			right: `${token('space.negative.025', '-2px')} !important`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			bottom: 'auto !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			left: 'auto !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			transform: 'none !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			willChange: 'auto !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"div[id^='react-select'] span": {
				whiteSpace: 'nowrap',
			},
		},
	},
});
